/* eslint-disable jsx-a11y/anchor-is-valid */
import { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Col, Container, Row } from 'react-bootstrap';
import { Link } from 'wouter';
import useLocation from 'wouter/use-location';
import { NewsletterForm } from '@medifind/forms';
import { IconFacebookAlt1 } from '@medifind/icons';
import { IconInstagramAlt1 } from '@medifind/icons';
import { IconLinkedInAlt1 } from '@medifind/icons';
import { IconYoutubeAlt1 } from '@medifind/icons';
import { IconTwitterAlt1 } from '@medifind/icons';
import { sitemapGetPagination } from '@medifind/interface';
import { MFLink, useFeatureFlag } from '@medifind/shared-basic-components';
import { isPrerender } from '@medifind/utils';
import BulletPin from '../assets/images/list-bullet-pin-white.png';
import { SitemapToggle } from './sitemap/SitemapToggle';
import styles from './Footer.module.scss';

const Footer = ({ smallFooterPaths, noMarginPaths }) => {
  const FEATURE_FLAG_PROFILE_MANAGEMENT = useFeatureFlag('PROFILE_MANAGEMENT');
  const [location] = useLocation();
  const smallFooter = !!(smallFooterPaths || []).some((p) => location.match(p));
  const noMargin = !!(noMarginPaths || []).some((p) => location.match(p));
  const showSitemap = location === '/';
  const [doctorsSitemapPagination, setDoctorsSitemapPagination] = useState(0);
  const [isSitemapExpanded, setSitemapExpanded] = useState(isPrerender);

  useMemo(() => {
    if (showSitemap)
      sitemapGetPagination().then((data) => {
        setDoctorsSitemapPagination(data.totalResults);
      });
  }, [showSitemap]);

  return (
    <footer className={classNames(styles['footer'], smallFooter && styles['footer--small'], noMargin && styles['footer--no-margin'])}>
      {!smallFooter && (
        <div className={styles['nav']}>
          <Container className={styles['container']}>
            {/* main links row, newsletter */}
            <Row className={styles['nav__row-gap']}>
              <Col sm={12} md={3}>
                <h5 className={styles['nav__title']}>For Patients</h5>
                <ul className={styles['nav__list']}>
                  <li>
                    <Link to="/medifind-story" id="medifind-story-link">
                      Our Story
                    </Link>
                  </li>
                  <li>
                    <Link to="/how-medifind-works" id="how-it-works-footer-link">
                      How MediFind Works
                    </Link>
                  </li>
                  <li>
                    <Link to="/directory/conditions" id="directory-conditions-footer-link">
                      Conditions A-Z
                    </Link>
                  </li>
                  <li>
                    <Link to="/directory/doctors" id="directory-doctors-footer-link">
                      Doctor Directory
                    </Link>
                  </li>
                  <li>
                    <Link to="/directory/symptoms" id="directory-symptoms-footer-link">
                      Symptoms Directory
                    </Link>
                  </li>
                  <li>
                    <Link to="/directory/procedures" id="directory-procedures-footer-link">
                      Procedures Directory
                    </Link>
                  </li>
                  <li>
                    <Link to="/faq" id="faq-footer-link">
                      FAQ
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact" id="feedback-footer-link">
                      Contact Us
                    </Link>
                  </li>
                </ul>
              </Col>
              <Col sm={12} md={3}>
                <h5 className={styles['nav__title']}>For Providers and Practices</h5>
                <ul className={styles['nav__list']}>
                  {FEATURE_FLAG_PROFILE_MANAGEMENT && (
                    <li>
                      <Link to="/profile-management/sign-up" id="profile-management-footer-link">
                        Claim Your Profile
                      </Link>
                    </li>
                  )}
                  <li>
                    <Link to="/news" id="news-footer-link">
                      Newsroom
                    </Link>
                  </li>
                </ul>
              </Col>
              <Col sm={12} md={3}>
                <h5 className={styles['nav__title']}>Business Solutions</h5>
                <ul className={styles['nav__list']}>
                  <li>
                    <MFLink to="https://www.phreesia.com/medifind/" target="_blank">
                      Provider
                    </MFLink>
                  </li>
                  <li>
                    <MFLink to="https://lifesciences.phreesia.com/medifind/" target="_blank">
                      Life Sciences
                    </MFLink>
                  </li>
                </ul>
              </Col>
              <Col sm={12} md={3}>
                <h5 className={styles['nav__title']}>Additional Resources</h5>
                <ul className={styles['nav__list']}>
                  <li>
                    <Link to="/health-data-privacy" id="health-data-privacy-footer-link">
                      Consumer Health Data Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/privacy" id="privacy-policy-footer-link">
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/terms" id="terms-footer-link">
                      Terms of Use
                    </Link>
                  </li>
                  <li>
                    <Link to="/policies" id="advertising-policy-footer-link">
                      Advertising Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/content-policy" id="content-policy-footer-link">
                      Content Policy
                    </Link>
                  </li>
                </ul>
              </Col>
            </Row>
            <Row className={styles['nav__mobile-divider']}>
              <Col>
                <div className={classNames(styles['nav__divider'], styles['nav__divider--mobile-divider'])}></div>
              </Col>
            </Row>
            <Row className={styles['nav__newsletter']}>
              <Col>
                <NewsletterForm
                  location={'Footer'}
                  classes={{
                    newsletter: styles['nav__newsletter__newsletter'],
                    title: styles['nav__newsletter__title'],
                    instructions: styles['nav__newsletter__instructions'],
                    inputLabel: styles['nav__newsletter__input-label'],
                    formFields: styles['nav__newsletter__form-fields'],
                    inputGroup: styles['nav__newsletter__input-group'],
                    disclaimer: styles['nav__newsletter__disclaimer'],
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <div className={styles['nav__divider']}></div>
              </Col>
            </Row>
            <Row className={styles['nav__follow-us']}>
              <Col>
                <div className={classNames(styles['nav__flex-space-between'], styles['nav__flex-space-between--mobile'])}>
                  <div>
                    <span className={styles['nav__colophon-logo']}>
                      <img src={BulletPin} alt="Bullet Pin" loading="lazy" />
                      MediFind
                    </span>
                  </div>
                  <div>
                    <div className={styles['nav__follow-us__text']}>Follow us on</div>
                    <span className={styles['nav__follow-us__social-media']}>
                      <MFLink
                        id="facebook-footer-link"
                        target="about:blank"
                        className={styles['social-media__link']}
                        href="https://www.facebook.com/medifindhealth/"
                        aria-label="MediFind Facebook"
                      >
                        <IconFacebookAlt1 />
                      </MFLink>
                    </span>
                    <span className={styles['nav__follow-us__social-media']}>
                      <MFLink
                        id="instagram-footer-link"
                        target="about:blank"
                        className={styles['social-media__link']}
                        href="https://www.instagram.com/medifindhealth"
                        aria-label="MediFind Instagram"
                      >
                        <IconInstagramAlt1 />
                      </MFLink>
                    </span>
                    <span className={styles['nav__follow-us__social-media']}>
                      <MFLink
                        id="youtube-footer-link"
                        target="about:blank"
                        className={styles['social-media__link']}
                        href="https://www.youtube.com/channel/UCbYmcsLXSFl0EkBdsTZ0nHQ"
                        aria-label="MediFind Youtube"
                      >
                        <IconYoutubeAlt1 />
                      </MFLink>
                    </span>
                    <span className={styles['nav__follow-us__social-media']}>
                      <MFLink
                        id="twitter-footer-link"
                        target="about:blank"
                        className={styles['social-media__link']}
                        href="https://www.twitter.com/medifindhealth"
                        aria-label="MediFind Twitter"
                      >
                        <IconTwitterAlt1 />
                      </MFLink>
                    </span>
                    <span className={styles['nav__follow-us__social-media']}>
                      <MFLink
                        id="linkedin-footer-link"
                        target="about:blank"
                        className={styles['social-media__link']}
                        href="https://www.linkedin.com/company/medifind/"
                        aria-label="MediFind LinkedIn"
                      >
                        <IconLinkedInAlt1 />
                      </MFLink>
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col>
                <div className={styles['nav__disclaimer']}>
                  This information is not intended as a substitute for informed medical advice. You should work with a licensed professional
                  to diagnose and treat health conditions. We let the data speak for itself, MediFind does not endorse any healthcare
                  providers.
                </div>
              </Col>
            </Row>
            <Row>
              <Col>
                <div className={classNames(styles['nav__flex-space-between'])}>
                  {showSitemap && (
                    <>
                      <SitemapToggle
                        isExpanded={isSitemapExpanded}
                        onToggle={() => {
                          setSitemapExpanded(!isSitemapExpanded);
                        }}
                      />
                    </>
                  )}
                  <div className={styles['nav__copyright']}>&copy; {new Date().getFullYear()} All Rights Reserved</div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      )}
      <div>
        {showSitemap && !!isSitemapExpanded && (
          <Container className={styles['container']}>
            <Row>
              <Col>
                <div>
                  <Link to={`/directory/doctors`} className={styles['sitemap-header']} id="doctor-profiles">
                    Doctor Profiles
                  </Link>
                  {doctorsSitemapPagination && (
                    <div className={styles['sitemap-content-container']}>
                      {Array.from({ length: doctorsSitemapPagination }, (_, index) => (
                        <Link
                          to={`/directory/sitemap/${index + 1}`}
                          id={`directory-doctor-${index + 1}`}
                          className={styles['sitemap-link']}
                          key={index}
                        >
                          {index + 1}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        )}
      </div>
      {smallFooter && (
        <>
          <div className={styles['small-footer']}>
            <Container>
              <Row>
                <Col
                  xs={12}
                  sm={12}
                  md={{ offset: 1, span: 5 }}
                  lg={{ offset: 0, span: 6 }}
                  xl={{ offset: 1, span: 5 }}
                  className={styles['small-footer__colophon']}
                >
                  <span className={styles['small-footer__colophon__logo']}>
                    <span className={styles['img']}></span>
                    MediFind
                  </span>
                  <span className={styles['small-footer__colophon__tagline']}>Better Care. Faster.</span>
                </Col>
                <Col
                  xs={12}
                  sm={12}
                  md={{ offset: 1, span: 5 }}
                  lg={{ offset: 0, span: 6 }}
                  xl={{ offset: 0, span: 6 }}
                  className={styles['small-footer__copy']}
                >
                  <p>&copy; {new Date().getFullYear()} All Rights Reserved.</p>
                </Col>
              </Row>
            </Container>
          </div>
          <div className={styles['small-footer__disclaimer']}>
            <Container>
              <Row>
                <Col md={{ span: 6, offset: 3 }}>
                  <p>
                    This information is not intended as a substitute for informed medical advice. You should work with a licensed
                    professional to diagnose and treat health conditions. We let the data speak for itself, MediFind does not endorse any
                    healthcare providers.
                  </p>
                </Col>
              </Row>
            </Container>
          </div>
        </>
      )}
    </footer>
  );
};

export default Footer;
